import React, { useState, useEffect, useMemo } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { getMasterplan } from "../../services/api";
import { useAuraVoice } from "../../hooks/useAuraVoice";
import "./Masterplan.css";

import { useGlobal } from "../../context/GlobalContext";

import Popup from "../../components/Popup/Popup";

// Importando os assets existentes
import Stars from "../../assets/stars.svg";
import Logo from "../../assets/logo.svg";
import Computer from "../../assets/feature_computer.svg";
import Pointer from "../../assets/feature_item.svg";
import Step1 from "../../assets/step_1.svg";
import Step2 from "../../assets/step_2.svg";
import Step3 from "../../assets/step_3.svg";
import Step4 from "../../assets/step_4.svg";
import Discovery from "../../assets/discovery_icon.svg";
import Build from "../../assets/build_icon.svg";
import Launch from "../../assets/launch_icon.svg";
import Green from "../../assets/green_check.svg";

// Logos de parceiros
import LogoB3 from "../../assets/original_logos/b3.svg";
import LogoColgate from "../../assets/original_logos/colgate.svg";
import LogoCorteva from "../../assets/original_logos/corteva.svg";
import LogoEmbraer from "../../assets/original_logos/embraer.svg";
import LogoIsa from "../../assets/original_logos/isa.svg";
import LogoJaguar from "../../assets/original_logos/jaguar.svg";
import LogoLoreal from "../../assets/original_logos/loreal.svg";
import LogoNestle from "../../assets/original_logos/nestle.svg";
import LogoNexa from "../../assets/original_logos/nexa.svg";
import LogoPetrobras from "../../assets/original_logos/petrobras.svg";
import LogoPorto from "../../assets/original_logos/porto-acu.svg";
import LogoPrio from "../../assets/original_logos/prio.svg";
import LogoPrudential from "../../assets/original_logos/prudential.svg";
import LogoPrumo from "../../assets/original_logos/prumo.svg";
import LogoSaintGobain from "../../assets/original_logos/saint-gobain.svg";
import LogoSaoMartinho from "../../assets/original_logos/sao-martinho.svg";
import LogoStellantis from "../../assets/original_logos/stellantis.svg";
// import LogoVolksagen from "../../assets/original_logos/volkswagen.svg";
import LogoWilson from "../../assets/original_logos/wilsonsons.svg";

const Masterplan = () => {
  const { state, dispatch } = useGlobal();
  const { id: routeId } = useParams();
  const [searchParams] = useSearchParams();
  const queryId = searchParams.get("id");
  const effectiveId = routeId ?? queryId ?? state.masterplan_id ?? null;
  const navigate = useNavigate();
  
  useAuraVoice("Pronto! Este é o seu Masterplan!", "default");
  
  const [emailOpen, setEmailOpen] = useState(false);
  const [loading, setLoading] = useState(Boolean(effectiveId));
  const [error, setError] = useState(null);

  function normalizeMasterplan(p) {
    const fd = p?.final_draft ?? {};
    const ai = fd.ai_vision ?? {};

    return {
      // ids / meta
      masterplan_id: p.id,
      user: p.user ?? "",
      company: p.company ?? "",
      email: p.email ?? "",

      // selections & options
      pains_options: p.pains_options ?? [],
      goals_options: p.goals_options ?? [],
      functions_options: p.functions_options ?? [],
      selected_pains: p.selected_pains ?? [],
      selected_goals: p.selected_goals ?? [],
      selected_functions: p.selected_functions ?? [],

      // top-level fields you likely show
      suggested_name: p.suggested_name ?? "",
      context: p.context ?? "",
      first_draft: p.first_draft ?? "",

      // nested final_draft flattened for your UI
      process_context: fd.process_context ?? "",
      ai_vision: {
        summary: ai.summary ?? "",
        steps: ai.steps ?? [],
      },
      features: fd.features ?? [],
      // outcomes in your payload is an array; if your UI expects a string, join it
      outcomes: Array.isArray(fd.outcomes) ? fd.outcomes : (fd.outcomes ?? []),
      roadmap: fd.roadmap ?? {
        discovery: "",
        build: "",
        launch: "",
      },

      // timestamps if needed
      created_at: p.created_at ?? null,
      updated_at: p.updated_at ?? null,
    };
  }

  useEffect(() => {
    if (!effectiveId) return;

    let cancelled = false;

    (async () => {
      setLoading(true);
      setError(null);
      try {
        const raw = await getMasterplan(effectiveId);
        const data = normalizeMasterplan(raw);
        if (!cancelled) {
          dispatch({ type: "HYDRATE_FROM_DB", payload: data });
        }
      } catch (e) {
        const msg = e?.response?.status
          ? `HTTP ${e.response.status}`
          : (e?.message || "Falha ao carregar o masterplan");
        if (!cancelled) setError(msg);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => { cancelled = true; };
  }, [effectiveId, dispatch]);

  const handleClick = () => {
    dispatch({ type: "RESET_STATE" });
    navigate("/");
  };

  if (loading) {
    return (
      <div className="masterplan-container" style={{ padding: 24 }}>
        <p>Carregando seu Masterplan…</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="masterplan-container" style={{ padding: 24 }}>
        <h2>Não foi possível carregar este Masterplan</h2>
        <p style={{ marginTop: 8, opacity: 0.8 }}>{String(error)}</p>
        <button className="primary-button masterplan-button" onClick={handleClick}>
          Voltar ao início
        </button>
      </div>
    );
  }

  // const handleClick = () => {
  //   dispatch({ type: "RESET_STATE" });
  //   navigate("/");
  // };

  function SolutionName() {
    const { state } = useGlobal();

    return <span>{state.suggested_name || "Aura"}</span>;
  }

  function AiContext() {
    const { state } = useGlobal();
    return <span>{state.ai_vision.summary || "—"}</span>;
  }

  function AiSteps() {
    const { state } = useGlobal();
    const steps = Array.isArray(state.ai_vision?.steps) ? state.ai_vision.steps : [];
    const icons = [Step1, Step2, Step3, Step4];

    return (
      <>
        {Array.from({ length: 4 }, (_, i) => (
          <div className="step-item" key={i}>
            <img src={icons[i] ?? Step1} alt={`Step ${i + 1}`} className="step-item-logo" />
            {steps[i] ?? "—"}
          </div>
        ))}
      </>
    );
  }

  function LeftFunctions() {
    const { state } = useGlobal();
    const steps = Array.isArray(state.features) ? state.features : [];
    
    return (
      <>
        {Array.from({ length: 3 }, (_, i) => (
          <div className="feature-item" key={i}>
            {steps[i] ?? "—"}
            <img src={Pointer} alt={`Funcionalidade ${i + 1}`} className="feature-item-logo" />
          </div>
        ))}
      </>
    );
  }

  function RightFunctions() {
    const { state } = useGlobal();
    const steps = Array.isArray(state.features) ? state.features : [];
    const subset = steps.slice(3, 6);
    
    return (
      <>
        {subset.map((text, i) => (
          <div className="feature-item" key={i}>
            <img src={Pointer} alt={`Funcionalidade ${i + 1}`} className="feature-item-logo" />
            {text || "—"}
          </div>
        ))}
      </>
    );
  }

  const PartnersCarousel2 = () => {
    const logos = [
        { src: LogoB3, alt: "B3" },
        { src: LogoColgate, alt: "Colgate" },
        { src: LogoCorteva, alt: "Corteva" },
        { src: LogoEmbraer, alt: "Embraer" },
        { src: LogoIsa, alt: "Isa" },
        { src: LogoJaguar, alt: "Jaguar" },
        { src: LogoLoreal, alt: "Loreal" },
        { src: LogoNestle, alt: "Nestle" },
        { src: LogoNexa, alt: "Nexa" },
        { src: LogoPetrobras, alt: "Petrobras" },
        { src: LogoPorto, alt: "Porto" },
        { src: LogoPrio, alt: "PRIO" },
        { src: LogoPrudential, alt: "Prudential" },
        { src: LogoPrumo, alt: "Prumo" },
        { src: LogoSaintGobain, alt: "SaintGobain" },
        { src: LogoSaoMartinho, alt: "SaoMartinho" },
        { src: LogoStellantis, alt: "Stellantis" },
        // { src: LogoVolksagen, alt: "Volkswagen" },
        { src: LogoWilson, alt: "WilsonSons" }
      ];
  
    return (
    <div className="partners-carousel-container">
      <motion.div 
        className="partners-carousel"
        animate={{ x: [0, -50 + "%"] }}
        transition={{
          x: {
            repeat: Infinity,
            repeatType: "loop",
            duration: 40,
            ease: "linear",
          },
        }}
        style={{ willChange: "transform" }}
      >
        {[...logos, ...logos, ...logos, ...logos].map((logo, index) => (
          <div key={index} className="partner-logo">
            <img src={logo.src} alt={logo.alt} />
          </div>
        ))}
      </motion.div>
    </div>
  );
  };


  return (
    <motion.div
              className="masterplan-container"
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -24 }}
              transition={{ duration: 0.5, ease: "easeOut" }}
            >
      <img
        src={Stars}
        alt="Estrelas no céu"
        className="masterplan-stars-background"
      />
      
      <h1 className="masterplan-title">Este é o seu Masterplan!</h1>

      <main className="masterplan-content">
        <div className="masterplan-section-name">
            <img src={Logo} alt="Logo" className="card-logo" />
            <p className="card-intro-text">
              Conheça a <span className="accent"><strong>{SolutionName()}</strong></span> 
            </p>
        </div>

        <div className="masterplan-section-context">
          <div className="top-side">
            <h2 className="accent">Seu processo AI First:</h2>
            <p>{AiContext()}</p>
          </div>
          <div className="bottom-side">
            <div className="left-side">
              {LeftFunctions()}
            </div>
            <div className="middle">
              <img src={Computer} alt="Computador" className="feature-computer-logo" />
            </div>
            <div className="right-side">
              {RightFunctions()}
            </div>
          </div>
        </div>

        <div className="masterplan-section-roadmap">
          <div className="top-side">
            <p>Roadmap de Implantação</p>
            <h2 className="accent">Sua solução pronta em 6 semanas!</h2>
          </div>
          <div className="bottom-side">
            <div className="roadmap-step">
              <img src={Discovery} alt="Ícone de Discovery" className="roadmap-step-logo" />
              <div className="roadmap-step-text">
                <p><strong>Discovery - Semana 1</strong></p>
                Nós analisamos profundamente seu processo, identificamos os principais gargalos e definimos um roteiro de solução personalizado.
              </div>
            </div>
            <div className="roadmap-step">
              <img src={Build} alt="Ícone de Build" className="roadmap-step-logo" />
              <div className="roadmap-step-text">
                <p><strong>Build - Semanas 2 a 5</strong></p>
                Nós construímos a solução com os módulos de tecnologia certos, adaptados às suas necessidades e integrados aos seus sistemas.
              </div>
            </div>
            <div className="roadmap-step">
              <img src={Launch} alt="Ícone de Launch" className="roadmap-step-logo" />
              <div className="roadmap-step-text">
                <p><strong>Launch - Semana 6</strong></p>
                Sua plataforma é implantada com treinamento e suporte para sua equipe, garantindo adoção imediata.
              </div>
            </div>
          </div>
        </div>

        <div className="masterplan-section">
          <div className="top-side">
            <img src={Green} alt="Coleção de Empresas" className="green-logo" /> <h1>Você está pronto!</h1>
          </div>
          <div className="bottom-side">
            <PartnersCarousel2 />
            <p>Faça parte da parceria que revoluciona <span className="accent">processos empresariais com IA integrada</span>.</p>
            <button onClick={() => setEmailOpen(true)} className="masterplan-popup-button">
              Quero crescer com IA!
            </button>

          </div>
          
        </div>

      </main>

      <Popup
        open={emailOpen}
        onClose={() => setEmailOpen(false)}
        />
    </motion.div>
    );
};

export default Masterplan;